import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  RowSelectionState,
  SortingState,
  TableOptions,
  useReactTable,
} from '@tanstack/react-table'
import { notUndefined, useVirtualizer } from '@tanstack/react-virtual'
import React, {
  CSSProperties,
  Fragment,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Form, Spinner } from 'react-bootstrap'
import SimpleBar from 'simplebar-react'
import styled from 'styled-components/macro'
import * as S from '../DataTable/DataTable/styles'
import { SortAscIcon, SortDescIcon } from '../DataTable/styles'
import {
  applyColumnMinWidths,
  resolveColumnDefId,
  resolveColumnMinWidth,
  useColumnWidthPersistence,
} from '../DataTable/columnSizing'
import { useAuth } from 'context/authentication/useAuth'
import { OverflownText } from 'components/OverflownText'
import Checkbox from '../Checkbox/checkbox'
import { HelpTooltip } from '../Tooltip/HelpTooltip'
import { cn } from '../UI/cn'

// Row hover context for cells that need to know if their row is hovered
const RowHoverContext = createContext<string | null>(null)

export function useIsRowHovered(rowId: string): boolean {
  const hoveredRowId = useContext(RowHoverContext)
  return hoveredRowId === rowId
}

export interface IClientDataTable<T> {
  columns: ColumnDef<T, any>[]
  data: T[]
  enableSorting?: boolean
  enableGlobalSearch?: boolean
  onTableBottomReached?: () => void
  stickyLastColumn?: boolean
  stickyFirstColumn?: boolean
  searchPlaceholder?: string
  enableRowSelection?: boolean
  onRowSelectionChange?: React.Dispatch<React.SetStateAction<RowSelectionState>>
  selectedRows?: RowSelectionState
  height?: number | string
  style?: CSSProperties
  smallRows?: boolean
  footerContent?: React.ReactNode
  emptyContent?: React.ReactNode
  loading?: boolean
  initialOrdering?: SortingState
  tableFooter?: React.JSX.Element
  tableHeader?: React.JSX.Element
  enableRightCellAlignment?: boolean
  disableBulkRowsSelection?: boolean
  selectionRowID?: keyof T
  invertedRowSelection?: boolean
  enableRowHover?: boolean
  /** DS2026: opt into drag-resizable columns with content-type min-width floors
   *  and device-local width persistence (mirrors the original DataTable). Default
   *  off, so existing ClientDataTable usages are unchanged. Pair with `tableKey`
   *  to persist widths. */
  enableColumnResizing?: boolean
  /** Fixed body row height in px. Defaults to 56 (or 40 with `smallRows`). */
  rowHeight?: number
  /** Fixed header row height in px. */
  headerHeight?: number
  /** Stable key used to namespace persisted column widths in localStorage. */
  tableKey?: string
}

export function ClientDataTable<T>(props: IClientDataTable<T>) {
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({})
  const [hoveredRowId, setHoveredRowId] = useState<string | null>(null)
  const { user } = useAuth()

  const resolvedRowHeight = props.rowHeight ?? (props.smallRows ? 40 : 56)

  const firstColumnId = useMemo(() => {
    const flagged = props.columns.find(
      (c) => (c.meta as { isFirstColumn?: boolean } | undefined)?.isFirstColumn
    )
    return resolveColumnDefId(flagged ?? props.columns[0])
  }, [props.columns])

  const [columnSizing, onColumnSizingChange] = useColumnWidthPersistence({
    tableKey: props.tableKey ?? 'client-data-table',
    userId: user?.id,
    enabled: !!props.enableColumnResizing && !!props.tableKey,
  })

  // Inject content-type minimum widths so TanStack's getSize() clamps to them.
  // ClientDataTable's bulk-select is a small inline checkbox (not the original
  // DataTable's bulk Actions control), so the first column uses its content-type
  // floor rather than the measured select-actions floor.
  const sizedColumns = useMemo(() => {
    if (!props.enableColumnResizing) return props.columns
    return applyColumnMinWidths(props.columns, {
      firstColumnId,
      rowSelectionEnabled: false,
    })
  }, [props.columns, props.enableColumnResizing, firstColumnId])

  const tableOptions: TableOptions<T> = {
    data: props.data ?? [],
    columns: sizedColumns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting: props.enableSorting,
    enableGlobalFilter: props.enableGlobalSearch,
    enableColumnResizing: props.enableColumnResizing ?? false,
    columnResizeMode: 'onChange',
    onColumnSizingChange: props.enableColumnResizing
      ? onColumnSizingChange
      : undefined,
    onRowSelectionChange: props.onRowSelectionChange ?? setSelectedRows,
    state: {
      rowSelection: props.selectedRows ?? selectedRows,
      ...(props.enableColumnResizing ? { columnSizing } : {}),
    },
    initialState: {
      sorting: undefined,
    },
    getRowId: (originalRow, index) =>
      props.selectionRowID !== undefined
        ? (originalRow[props.selectionRowID] as string)
        : index.toString(),
  }

  if (props.initialOrdering) {
    tableOptions.initialState!.sorting = props.initialOrdering
  }

  const table = useReactTable(tableOptions)

  // Virtualization
  const tableContainerRef = useRef<HTMLDivElement>(null)
  const tableRef = useRef<HTMLTableElement>(null)
  const { rows: originalRows } = table.getRowModel()

  const virtualizer = useVirtualizer<HTMLDivElement, HTMLTableRowElement>({
    count: originalRows.length,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => resolvedRowHeight,
    overscan: 5,
    scrollMargin: 0,
    measureElement:
      typeof window !== 'undefined' &&
      navigator.userAgent.indexOf('Firefox') === -1
        ? (element) => element?.getBoundingClientRect().height
        : undefined,
  })

  const virtualRows = virtualizer.getVirtualItems()

  const [before, after] = useMemo(() => {
    return virtualRows.length > 0
      ? [
          notUndefined(virtualRows[0]).start - virtualizer.options.scrollMargin,
          virtualizer.getTotalSize() -
            notUndefined(virtualRows[virtualRows.length - 1]).end,
        ]
      : [0, 0]
  }, [
    virtualRows,
    virtualizer.options.scrollMargin,
    virtualizer.getTotalSize(),
  ])

  const onTableBottomReached = props.onTableBottomReached
  const manageShadows = useCallback(
    (event?: React.UIEvent<HTMLDivElement, UIEvent>) => {
      const scrollElement = event?.currentTarget ?? tableContainerRef.current
      if (!scrollElement) return

      const {
        scrollHeight,
        scrollTop,
        clientHeight,
        scrollLeft,
        scrollWidth,
        clientWidth,
      } = scrollElement

      // Show left shadow when scrolled away from the left edge
      if (scrollLeft > 0) {
        document
          .querySelectorAll('.shadowable-table-cell')
          .forEach((el) => el?.classList.add('shadow-table-cell'))
      } else {
        document
          .querySelectorAll('.shadowable-table-cell')
          .forEach((el) => el?.classList.remove('shadow-table-cell'))
      }

      // Show right shadow only when there's horizontal overflow AND not scrolled to the right edge
      const hasHorizontalOverflow = scrollWidth > clientWidth
      const isScrolledToRight =
        Math.abs(scrollLeft - (scrollWidth - clientWidth)) < 1
      if (hasHorizontalOverflow && !isScrolledToRight) {
        document
          .querySelectorAll('.shadowable-table-cell-right')
          .forEach((el) => el?.classList.add('shadow-table-cell-right'))
      } else {
        document
          .querySelectorAll('.shadowable-table-cell-right')
          .forEach((el) => el?.classList.remove('shadow-table-cell-right'))
      }

      // called once the user has scrolled within 300px of the bottom of the table
      if (scrollHeight - scrollTop - clientHeight < 300) {
        onTableBottomReached?.()
      }
    },
    [onTableBottomReached]
  )

  useEffect(() => {
    // The virtualizer renders in two phases: the first render only has the
    // header (virtualRows is empty until the scroll container is measured),
    // then a second render adds the body rows. A one-shot rAF on mount fires
    // during phase one and only sees the header cell, leaving body cells
    // unshadowed until the user scrolls. Observing the table's size catches
    // phase two — and any later content changes — without depending on
    // virtualizer-internal state.
    const frame = requestAnimationFrame(() => manageShadows())
    const tableEl = tableRef.current
    const observer = tableEl ? new ResizeObserver(() => manageShadows()) : null
    if (observer && tableEl) observer.observe(tableEl)
    return () => {
      cancelAnimationFrame(frame)
      observer?.disconnect()
    }
  }, [props.data, props.columns, manageShadows])

  // Bottom reached callback
  const handleScroll = (event?: React.UIEvent<HTMLDivElement, UIEvent>) => {
    manageShadows(event)
  }

  return (
    <div className={'flex flex-col w-full relative flex-1 overflow-auto'}>
      {props?.tableHeader}

      {props.enableGlobalSearch && (
        <div className={'px-3 bg-gray-100 pt-3 pb-6 -mb-3 rounded'}>
          <Form.Control
            placeholder={props.searchPlaceholder ?? 'Search...'}
            onChange={(e) => table.setGlobalFilter(e.target.value)}
            value={table.getState().globalFilter}
          />
        </div>
      )}

      <div
        data-table-frame="true"
        data-row-selection={props.enableRowSelection ? 'true' : 'false'}
        style={{
          border: '1px solid #eaecf0',
          borderRadius: 8,
          overflow: 'hidden',
          height: props.height || 480,
          maxHeight: props.height || 480,
        }}
      >
        <SimpleBar
          style={{
            height: props.height || 480,
            maxHeight: props.height || 480,
            ...props.style,
          }}
          autoHide={false}
          scrollableNodeProps={{
            ref: tableContainerRef,
            onScroll: handleScroll,
          }}
        >
          <S.Table ref={tableRef}>
            <S.THead>
              {table.getHeaderGroups().map((headerGroup) => {
                const filteredHeaderGroup = headerGroup.headers
                return (
                  <S.TRHead
                    key={`headergroup_${headerGroup.id}`}
                    style={
                      props.headerHeight
                        ? { height: props.headerHeight }
                        : undefined
                    }
                  >
                    {filteredHeaderGroup.map((header, headerIndx) => {
                      const sortingProps: Record<string, unknown> = {}

                      if (header.column.getCanSort()) {
                        sortingProps['onClick'] = () =>
                          header.column.toggleSorting()
                        sortingProps['style'] = { cursor: 'pointer' }
                      }

                      const tooltip = (header?.column.columnDef?.meta as any)
                        ?.tooltip
                      const headerMeta = header?.column.columnDef?.meta as
                        | { isLastColumn?: boolean; columnType?: string }
                        | undefined
                      const isActionsHeader =
                        !!headerMeta?.isLastColumn ||
                        headerMeta?.columnType === 'actions'

                      return (
                        <S.TH
                          data-actions-header={isActionsHeader || undefined}
                          className={cn({
                            'sticky-table-cell':
                              headerIndx === 0 && props.stickyFirstColumn,
                            'sticky-table-cell-right':
                              props.stickyLastColumn &&
                              headerIndx === headerGroup.headers.length - 1,
                            'shadowable-table-cell':
                              headerIndx === 0 && props.stickyFirstColumn,
                            'shadowable-table-cell-right':
                              props.stickyLastColumn &&
                              headerIndx === headerGroup.headers.length - 1,
                          })}
                          style={{
                            width: header.getSize(),
                            ...(props.enableColumnResizing
                              ? {
                                  minWidth: resolveColumnMinWidth(
                                    header.column.columnDef,
                                    {}
                                  ),
                                  // Cap the header to the same width as the body
                                  // cells so the two tracks can't diverge (kept
                                  // the actions column header/body aligned).
                                  maxWidth: header.getSize(),
                                }
                              : {}),
                            pointerEvents: props.data.length ? 'all' : 'none',
                            ...(headerIndx === 0 && props.stickyFirstColumn
                              ? { left: 0, zIndex: 21 }
                              : {}),
                            ...(props.stickyLastColumn &&
                            headerIndx === headerGroup.headers.length - 1
                              ? { right: 0, zIndex: 21 }
                              : {}),
                          }}
                          key={header.id}
                          colSpan={header.colSpan}
                        >
                          {!header.isPlaceholder && (
                            <div
                              data-column-header
                              className={cn(
                                'flex items-center justify-start nowrap',
                                {
                                  'justify-end': (
                                    header?.column.columnDef?.meta as any
                                  )?.rightAlign,
                                }
                              )}
                            >
                              {props.enableRowSelection &&
                                !props.disableBulkRowsSelection &&
                                headerIndx === 0 && (
                                  <Checkbox
                                    wrapperClassName={'mr-2'}
                                    {...{
                                      checked: props.invertedRowSelection
                                        ? !table.getIsAllRowsSelected()
                                        : table.getIsAllRowsSelected(),
                                      // checked: table.getIsAllRowsSelected(),
                                      indeterminate:
                                        table.getIsSomeRowsSelected(),
                                      onChange: () => {
                                        const allSelected =
                                          table.getIsAllRowsSelected()
                                        table.toggleAllRowsSelected(
                                          !allSelected
                                        )
                                      },
                                    }}
                                  />
                                )}

                              <S.HeadingText {...sortingProps}>
                                {flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                              </S.HeadingText>

                              {tooltip ? (
                                <div style={{ marginLeft: 10 }}>
                                  <HelpTooltip
                                    title={tooltip?.title}
                                    content={tooltip?.content}
                                    link={{
                                      text: tooltip?.link_text,
                                      href: tooltip?.link,
                                    }}
                                  />
                                </div>
                              ) : null}
                              <div {...sortingProps}>
                                {header.column.getCanSort() &&
                                  header.column.getIsSorted() &&
                                  (header.column.getIsSorted() === 'asc' ? (
                                    <SortDescIcon />
                                  ) : (
                                    <SortAscIcon />
                                  ))}
                              </div>
                            </div>
                          )}
                          {props.enableColumnResizing &&
                            header.column.getCanResize() && (
                              <S.ColumnResizeHandle
                                role="separator"
                                aria-orientation="vertical"
                                aria-label="Resize column"
                                data-resizing={
                                  header.column.getIsResizing() || undefined
                                }
                                onMouseDown={header.getResizeHandler()}
                                onTouchStart={header.getResizeHandler()}
                                onClick={(e) => e.stopPropagation()}
                              />
                            )}
                        </S.TH>
                      )
                    })}
                  </S.TRHead>
                )
              })}
            </S.THead>
            <RowHoverContext.Provider
              value={props.enableRowHover ? hoveredRowId : null}
            >
              <S.TBody
                style={{
                  height: `${virtualizer.getTotalSize()}px`,
                }}
              >
                {originalRows.length > 0 ? (
                  <>
                    {before > 0 && (
                      <S.TR>
                        <S.TD style={{ height: `${before}px` }} />
                      </S.TR>
                    )}
                    {virtualRows.map((_row) => {
                      const row = originalRows[_row.index]
                      const cells = [...row.getVisibleCells()]
                      return (
                        <Fragment key={row.id}>
                          <S.TR
                            key={_row.key}
                            data-index={_row.index}
                            ref={virtualizer.measureElement}
                            isSelected={row.getIsSelected()}
                            style={{
                              height: `${resolvedRowHeight}px`,
                            }}
                            onMouseEnter={
                              props.enableRowHover
                                ? () => setHoveredRowId(row.id)
                                : undefined
                            }
                            onMouseLeave={
                              props.enableRowHover
                                ? () => setHoveredRowId(null)
                                : undefined
                            }
                          >
                            {cells.map((cell, indx) => {
                              const meta = cell?.column.columnDef?.meta as any
                              const rightAlign = meta?.rightAlign
                              // For plain string/number cells, surface the full
                              // value as a tooltip when the text is truncated.
                              const rawCellValue = cell.getValue()
                              const cellTooltip =
                                typeof rawCellValue === 'string' &&
                                rawCellValue.trim()
                                  ? rawCellValue
                                  : typeof rawCellValue === 'number'
                                    ? String(rawCellValue)
                                    : undefined
                              return (
                                <S.TD
                                  className={cn({
                                    'sticky-table-cell':
                                      indx === 0 && props.stickyFirstColumn,
                                    'shadowable-table-cell':
                                      indx === 0 && props.stickyFirstColumn,
                                    'sticky-table-cell-right':
                                      props.stickyLastColumn &&
                                      indx === cells.length - 1,
                                    'shadowable-table-cell-right':
                                      props.stickyLastColumn &&
                                      indx === cells.length - 1,
                                    'text-right': rightAlign,
                                  })}
                                  key={cell.id}
                                  style={{
                                    height: `${resolvedRowHeight}px`,
                                    ...(props.enableColumnResizing
                                      ? {
                                          width: cell.column.getSize(),
                                          maxWidth: cell.column.getSize(),
                                          minWidth: resolveColumnMinWidth(
                                            cell.column.columnDef,
                                            {}
                                          ),
                                        }
                                      : {}),
                                  }}
                                >
                                  <div
                                    className={cn('flex items-center min-w-0', {
                                      'justify-end':
                                        rightAlign &&
                                        props.enableRightCellAlignment,
                                      // Center the trailing actions/kebab column
                                      // so the cell lines up under its centered
                                      // "Actions" header.
                                      'justify-center':
                                        meta?.isLastColumn ||
                                        meta?.columnType === 'actions',
                                    })}
                                  >
                                    {props.enableRowSelection && indx === 0 && (
                                      <Checkbox
                                        wrapperClassName={cn('mr-2', {
                                          'ml-2 mr-0':
                                            rightAlign &&
                                            props.enableRightCellAlignment,
                                        })}
                                        {...{
                                          checked: props.invertedRowSelection
                                            ? !row.getIsSelected()
                                            : row.getIsSelected(),
                                          // checked: row.getIsSelected(),
                                          disabled: !row.getCanSelect(),
                                          onChange: () => {
                                            if (row.getCanSelect()) {
                                              row.toggleSelected()
                                            }
                                          },
                                        }}
                                      />
                                    )}
                                    {props.enableColumnResizing &&
                                    !meta?.multiLine &&
                                    !meta?.isLastColumn &&
                                    meta?.columnType !== 'actions' ? (
                                      // On resizable tables, cell content
                                      // truncates to one line (…) and reveals the
                                      // full value on hover (OverflownText only
                                      // expands when actually overflowing).
                                      // Pills/badges are block/flex children so
                                      // only inline text ellipsizes. Actions/last
                                      // columns (kebab menus) are excluded so they
                                      // keep their centering. Opt out per column
                                      // via meta.multiLine.
                                      <OverflownText
                                        maxLines={1}
                                        tooltipText={cellTooltip}
                                        className="flex-1"
                                      >
                                        {flexRender(
                                          cell.column.columnDef.cell,
                                          cell.getContext()
                                        ) || ' - '}
                                      </OverflownText>
                                    ) : (
                                      flexRender(
                                        cell.column.columnDef.cell,
                                        cell.getContext()
                                      ) || ' - '
                                    )}
                                  </div>
                                </S.TD>
                              )
                            })}
                          </S.TR>
                        </Fragment>
                      )
                    })}
                    {after > 0 && (
                      <S.TR>
                        <S.TD style={{ height: `${after}px` }} />
                      </S.TR>
                    )}
                  </>
                ) : (
                  <tr>
                    <td colSpan={props.columns.length}>
                      <div
                        className={
                          'flex items-center justify-center w-full h-40 text-base text-gray-400 select-none'
                        }
                      >
                        {props.emptyContent ?? 'No data to show.'}
                      </div>
                    </td>
                  </tr>
                )}
              </S.TBody>
            </RowHoverContext.Provider>
          </S.Table>
          {props?.footerContent && <S.TFooter>{props.footerContent}</S.TFooter>}
        </SimpleBar>
      </div>
      {props?.tableFooter}
      <LoadingOverlay show={props.loading}>
        <Spinner />
      </LoadingOverlay>
    </div>
  )
}

const LoadingOverlay = styled.div<{
  show?: boolean
}>`
  display: ${({ show }) => (show ? 'flex' : 'none')};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(1px);
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  z-index: 10; /* Local loading overlay */
`
